// Import required dependencies from React and notistack for notifications
import React, { createContext, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import API_BASE_URL from '../config/api';

// Create a context for managing notifications across components
export const NotificationContext = createContext();

// NotificationProvider component to manage notification state and API interactions
export const NotificationProvider = ({ children }) => {
  // State for storing notifications, loading status, and errors
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Hook for displaying snackbar notifications
  const { enqueueSnackbar } = useSnackbar();

  // Calculate unread notification count
  const unreadCount = notifications.filter(n => !n.IsRead).length;

  // Fetch notifications from the API with JWT authentication
  const fetchNotifications = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      // Silently skip if not logged in
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {

      const response = await fetch(`${API_BASE_URL}/api/Notification`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      // Handle HTTP errors
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Please log in again.');
        } else if (response.status === 404) {
          throw new Error('Notification endpoint not found.');
        }
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }

      // Validate response content type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Invalid response format: Expected JSON');
      }

      const data = await response.json();
      // Normalize API response to ensure consistent data structure
      const newNotifications = Array.isArray(data) ? data : data.Data || [];
      const normalizedNotifications = newNotifications.map((notif) => ({
        Id: notif.id,
        UserId: notif.userId,
        Message: notif.message,
        IsError: notif.isError,
        NotificationType: notif.notificationType,
        Timestamp: notif.timestamp,
        IsRead: notif.isRead,
      }));

      // Update notifications and show snackbar for new, unread notifications
      setNotifications((prev) => {
        const prevIds = new Set(prev.map((n) => n.Id));
        normalizedNotifications.forEach((notif) => {
          if (!prevIds.has(notif.Id) && !notif.IsRead) {
            enqueueSnackbar(`${notif.NotificationType === 1 ? '📢' : '✅'} ${notif.Message}`, {
              variant: notif.IsError ? 'error' : notif.NotificationType === 1 ? 'info' : 'success',
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
            });
          }
        });
        return normalizedNotifications;
      });

      setError(null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(`Failed to load notifications: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Mark a specific notification as read via API
  const markAsRead = async (id) => {
    try {
      const notification = notifications.find((n) => n.Id === id);
      if (!notification) {
        console.error(`Notification with ID ${id} not found`);
        return;
      }

      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError('Authentication token is missing. Please log in.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/Notification/markread`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ ...notification, IsRead: true }),
      });

      // Validate response
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          throw new Error('Invalid response format: Expected JSON');
        }
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }

      const data = await response.json();
      if (data.Success) {
        // Update local state to reflect the notification as read
        setNotifications((prev) =>
          prev.map((n) => (n.Id === id ? { ...n, IsRead: true } : n))
        );
      } else {
        console.error('Failed to mark notification as read:', data.Message);
        setError(data.Message || 'Failed to mark notification as read');
      }
    } catch (error) {
      console.error('Error updating notification:', error);
      setError(`Failed to update notification: ${error.message}`);
    }
  };

  // Add a new process notification via API
  const addProcessNotification = async (message, isError = false) => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in.');
      }

      // Create a new notification object
      const notification = {
        Message: message,
        IsError: isError,
        NotificationType: 0, // Process notification
        Timestamp: new Date().toISOString(),
        IsRead: false,
      };

      const response = await fetch(`${API_BASE_URL}/api/Notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(notification),
      });

      if (!response.ok) {
        throw new Error(`Failed to save notification: ${response.status}`);
      }

      const data = await response.json();
      if (data.Success) {
        // Show snackbar for the new notification and refresh the notification list
        enqueueSnackbar(`✅ ${message}`, {
          variant: isError ? 'error' : 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        await fetchNotifications();
      } else {
        throw new Error(data.Message || 'Failed to save notification');
      }
    } catch (error) {
      console.error('Error adding process notification:', error);
      setError(`Failed to add notification: ${error.message}`);
    }
  };

  // Add a structured notification with event type and metadata
  const addStructuredNotification = async (eventType, metadata = {}, isError = false, notificationType = 0, customMessage = null) => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in.');
      }

      // Validate eventType
      if (!eventType || typeof eventType !== 'string') {
        throw new Error('EventType must be a non-empty string');
      }

      // Ensure metadata is an object
      const safeMetadata = metadata && typeof metadata === 'object' && !Array.isArray(metadata)
        ? metadata
        : {};

      // Create a structured notification request
      const notificationRequest = {
        EventType: eventType,
        Message: customMessage || '', // Will be generated on backend if empty
        IsError: isError,
        NotificationType: notificationType,
        Metadata: safeMetadata,
      };

      console.log('Sending notification request:', notificationRequest);

      const response = await fetch(`${API_BASE_URL}/api/Notification/structured`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(notificationRequest),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.Message || errorText;
        } catch {
          errorMessage = errorText;
        }
        throw new Error(`Failed to save structured notification: ${response.status} - ${errorMessage}`);
      }

      const data = await response.json();
      if (data.Success) {
        // Show snackbar for the new notification using the generated message
        const displayMessage = data.GeneratedMessage || customMessage || eventType;
        enqueueSnackbar(`${isError ? '❌' : '✅'} ${displayMessage}`, {
          variant: isError ? 'error' : 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        await fetchNotifications();
        return data.GeneratedMessage;
      } else {
        throw new Error(data.Message || 'Failed to save structured notification');
      }
    } catch (error) {
      console.error('Error adding structured notification:', error);
      setError(`Failed to add notification: ${error.message}`);
      throw error;
    }
  };

  // Simple notification helper for displaying snackbar messages
  const showNotification = (message, variant = 'info') => {
    enqueueSnackbar(message, {
      variant: variant,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
    });
  };

  // Fetch notifications on component mount and set up polling (only if logged in)
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      fetchNotifications();

      // Set up polling to check for new notifications every 30 seconds
      const pollInterval = setInterval(() => {
        const currentToken = localStorage.getItem('jwtToken');
        if (currentToken) {
          fetchNotifications();
        }
      }, 30000); // 30 seconds

      // Cleanup interval on unmount
      return () => clearInterval(pollInterval);
    } else {
      setLoading(false);
    }
  }, []);

  // Provide notification context to child components
  return (
    <NotificationContext.Provider
      value={{ notifications, setNotifications, markAsRead, loading, error, addProcessNotification, addStructuredNotification, unreadCount, showNotification, fetchNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Export the NotificationProvider as default
export default NotificationProvider;